<script setup>
import { computed } from 'vue';
import AudioChip from 'next/message/chips/Audio.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  appearance: {
    type: String,
    required: true,
  },
  isOutgoing: {
    type: Boolean,
    required: true,
  },
  avatarName: {
    type: String,
    default: '',
  },
  avatarSrc: {
    type: String,
    default: '',
  },
  // Aceleres: nome de quem gravou, preenchido só em conversa de GRUPO (ver
  // Audio.vue). Vazio em qualquer outro contexto — inclusive na prévia do
  // menu de contexto, que monta este componente sem a prop —, e aí nenhum
  // rótulo é desenhado e o cartão fica idêntico ao que já era.
  senderName: {
    type: String,
    default: '',
  },
  senderColorClass: {
    type: String,
    default: '',
  },
  forwarded: {
    type: Boolean,
    default: false,
  },
  uploadedAudio: {
    type: Boolean,
    default: false,
  },
  showMessageMeta: {
    type: Boolean,
    default: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

// Aceleres 2026-09-09: o rotulo fecha com dois pontos, como nas outras bolhas
// (ver `senderNameLabel` no Base.vue). A prop `senderName` continua a ser o
// nome limpo — e ela que vai no `title` do elemento.
const senderNameLabel = computed(() =>
  props.senderName ? `${props.senderName}:` : props.senderName
);
</script>

<template>
  <AudioChip
    v-bind="$attrs"
    :attachment="attachment"
    :appearance="appearance"
    :show-message-meta="showMessageMeta"
    :compact="compact"
    class="text-n-slate-12 skip-context-menu"
  >
    <template v-if="senderName" #audio-header>
      <p
        class="aceleres-audio-sender"
        :class="senderColorClass"
        :title="senderName"
      >
        {{ senderNameLabel }}
      </p>
    </template>

    <template v-if="isOutgoing" #audio-leading>
      <span
        v-if="uploadedAudio || forwarded"
        class="aceleres-audio-voice-avatar"
        :class="{ '!bg-[#ffa928]': uploadedAudio }"
        aria-hidden="true"
      >
        <Icon :icon="uploadedAudio ? 'i-lucide-headphones' : 'i-lucide-mic'" />
      </span>
      <div
        v-else
        class="aceleres-audio-avatar-wrap aceleres-audio-avatar-wrap--outgoing"
      >
        <Avatar :name="avatarName" :src="avatarSrc" :size="50" rounded-full />
        <span class="aceleres-audio-mic-icon">
          <Icon icon="i-lucide-mic" />
        </span>
      </div>
    </template>

    <template v-else #audio-trailing>
      <span
        v-if="uploadedAudio"
        class="aceleres-audio-voice-avatar !bg-[#ffa928]"
        aria-hidden="true"
      >
        <Icon icon="i-lucide-headphones" />
      </span>
      <div
        v-else
        class="aceleres-audio-avatar-wrap aceleres-audio-avatar-wrap--incoming"
      >
        <Avatar :name="avatarName" :src="avatarSrc" :size="50" rounded-full />
        <span class="aceleres-audio-mic-icon">
          <Icon icon="i-lucide-mic" />
        </span>
      </div>
    </template>
  </AudioChip>
</template>

<style scoped>
/* Aceleres: o nome do participante do grupo é o TÍTULO do cartão do áudio —
   mesma medida (16,2px) e mesmo peso que o nome sobre a foto e a figurinha,
   para que o grupo não tenha dois tamanhos de autoria na mesma rolagem.

   Fica DENTRO do cartão do player, e não no BaseBubble: a bolha do áudio é
   transparente (o cartão é desenhado pelo chip), então o rótulo do Base sairia
   solto sobre o fundo da conversa.

   `min-width: 0` + `overflow: hidden` porque o cartão é flex: sem isso um nome
   longo estica o cartão em vez de reticenciar. */
.aceleres-audio-sender {
  margin: 0 0 4px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16.2px;
  font-weight: 600;
  line-height: 1.2;
  color: #000;
  opacity: 0.9;
}

/* 50px nos DOIS sentidos: a foto ocupa a mesma area no recebido e no enviado,
   mantendo a linha do player com a mesma altura visual. */
.aceleres-audio-avatar-wrap {
  position: relative;
  flex-shrink: 0;
  width: 50px;
  height: 50px;
}

/* O Avatar tem DOIS spans: a raiz (que o CSS redimensiona) e, dentro dela, o
   container com `rounded-full` e `overflow: hidden` que e quem realmente
   desenha o circulo. O container e item flex da raiz, entao o `flex-shrink`
   encolhe so a LARGURA dele quando a raiz e menor que o prop `size`; a altura
   fica com o valor inline do `size` e o circulo vira elipse. Aconteceu em
   02/09, quando a linha do player passou a 50px e o `size` continuou 64: a
   foto ficou 50x64. Amarrar o container a raiz mantem o circulo redondo
   qualquer que seja a medida da raiz -- inclusive os 54px que o
   `.acl-whatsapp-mobile-scope` forca em aceleres-overrides.css. */
.aceleres-audio-avatar-wrap > :deep(span:first-child) > span {
  width: 100% !important;
  height: 100% !important;
}

/* Aceleres 2026-09-02: o microfone deixou de ter disco atras dele — nem o
   verde #00a884 do enviado, nem o azul #53bdeb do recebido, nem o anel branco
   de 2px que os dois partilhavam. Fica so o desenho, 19x26, branco.

   O "contorno branco" pedido nao precisa de nada acrescentado: `i-lucide-mic`
   e um icone de CONTORNO desenhado por mascara CSS, sem preenchimento, entao
   o `color` pinta exatamente os tracos do microfone.

   Estas regras tem um gemeo `!important` em `public/aceleres-overrides.css`
   (bloco `.aceleres-audio-mic-icon`), e e ELE que vence — folha global com
   `!important` ganha de estilo `scoped` sem ele. Ficam iguais de proposito:
   enquanto divergiram, o microfone verde com halo `#d9fdd3` escrito aqui era
   codigo morto e enganava quem viesse ajustar o icone. O recorte do `viewBox`
   da mascara, que faz os 19x26 serem a medida do DESENHO e nao de uma caixa
   com folga, esta explicado la — e so la, para o desenho nao ter duas fontes. */
.aceleres-audio-mic-icon {
  position: absolute;
  bottom: -1px;
  width: 19px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  color: #fff;
  background: transparent;
  pointer-events: none;
  /* Contorno do verde do cartao enviado (`.right-bubble .acl-wa-audio`), a
     volta do microfone. `drop-shadow` e nao `box-shadow`: o icone e uma
     mascara, e `box-shadow` desenharia o retangulo de 19x26 em vez do desenho.

     Vale para os dois sentidos, e independente da cor do desenho: o enviado
     ficou cinza na regra mais abaixo, o recebido continua branco, e o halo
     serve os dois — o que ele separa e a FOTO por baixo.

     Esta declaracao PRECISA de existir tambem no `aceleres-overrides.css`, e
     nao so aqui. Foi assim que o halo anterior (`#d9fdd3` a volta de um
     microfone verde) sobreviveu meses depois de todo o resto da regra ter sido
     substituido: o overrides redeclarava tudo com `!important` menos o
     `filter`, e essa unica propriedade continuava a vir daqui, invisivel para
     quem so lesse um dos dois ficheiros. */
  filter: drop-shadow(1px 0 0 #d9fdd3) drop-shadow(-1px 0 0 #d9fdd3)
    drop-shadow(0 1px 0 #d9fdd3) drop-shadow(0 -1px 0 #d9fdd3);
}

/* O avatar do enviado usa a mesma medida do recebido. O override publico
   repete esta regua porque vence por `!important` no runtime. */
.aceleres-audio-avatar-wrap--outgoing {
  width: 50px;
  height: 50px;
}

.aceleres-audio-avatar-wrap--outgoing .aceleres-audio-mic-icon {
  right: -2px;
  color: #667781;
  /* O halo verde de 1px continua, herdado da regra base: o que tirou o aspecto
     grosso foi afinar o traco do desenho (3 -> 1,5), nao remover o contorno.
     A mascara fina e a capsula cheia estao so no aceleres-overrides.css, junto
     do recorte do viewBox — o desenho tem uma fonte so. */
}

/* O microfone do recebido mantém a cor verde da conversa, mas usa a mesma
   caixa do enviado para que os dois players tenham o mesmo peso visual. */
.aceleres-audio-avatar-wrap--incoming .aceleres-audio-mic-icon {
  left: -2px;
  width: 15px;
  height: 26px;
  color: #1daa61;
  filter: none;
}

.aceleres-audio-avatar-wrap--incoming
  .aceleres-audio-mic-icon
  :deep(:is(svg, span)) {
  width: 18px;
  height: 26px;
  -webkit-mask-size: contain;
  mask-size: contain;
}

.aceleres-audio-mic-icon :deep(svg),
.aceleres-audio-mic-icon :deep(span) {
  width: 19px;
  height: 26px;
}

.aceleres-audio-voice-avatar {
  display: inline-flex;
  width: 50px;
  height: 50px;
  flex: 0 0 50px;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: #fff;
  background: #ff5e3a;
}

.aceleres-audio-voice-avatar :deep(svg),
.aceleres-audio-voice-avatar :deep(span) {
  width: 25px;
  height: 25px;
}
</style>
